/**
 * DualRoleTabBar — sticky "With my therapist" / "At Home" switcher.
 *
 * Renders across every parent-facing route (not just /parent-home) so a
 * dual-role parent never loses the ability to jump between their
 * Clinical view and their PFP view. Previously the tabs only existed on
 * /parent-home; the moment the parent clicked into any child page or
 * PFP sub-page the tabs disappeared and never came back.
 *
 * Hidden when:
 *   • the user isn't dual-role (single-role parents don't need it);
 *   • the user isn't logged in;
 *   • we're on an auth/onboarding/redirect route where a top nav
 *     would be visually disruptive.
 *
 * The "active" state is a heuristic: any PFP route → PFP, otherwise
 * Clinical. Simple, stateless, and matches what the user sees.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { Stethoscope, Heart } from 'lucide-react';

const HIDDEN_PATH_PREFIXES = [
  '/login',
  '/parent-app/login',
  '/parent-app/signup',
  '/parent-app/onboarding',
  '/parent-app/accept-invite',
  '/parent-app/auth-callback',
  '/parent-app/pricing',
  '/parent-app',            // exact welcome landing — see check below
  '/privacy',
  '/resources',
];

// Any of these prefixes = PFP tab is the active one. Excludes the
// "With my therapist" sub-route because that lives inside PFP but
// is a distinct tab — see isTherapistTab below.
const PFP_ROUTE_PREFIXES = [
  '/parent-app',
];

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={
        'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ' +
        (active
          ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-200'
          : 'text-stone-600 hover:text-stone-900 hover:bg-white/70')
      }
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

export default function DualRoleTabBar({ user: userProp }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Read user from localStorage as a fallback — App.js only bootstraps
  // the `user` state on mount, so a fresh login inside the same page
  // load leaves the prop stale. Localstorage is the actual source of
  // truth after ParentAppLogin runs setItem + navigate.
  let user = userProp;
  if (!user) {
    try {
      user = JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      user = null;
    }
  }

  // Bail early on unauthenticated or single-role users.
  const caps = user?.capabilities || {};
  const dualRole = Boolean(caps.pfp && caps.clinical_parent);
  if (!user || !dualRole) return null;

  // Hide on auth/onboarding surfaces.
  const path = location.pathname;
  // Special-case `/parent-app` exactly — welcome page. Every deeper
  // /parent-app/* IS a PFP page we want to keep the tabs on.
  const hideExact = path === '/parent-app';
  const hidePrefix = HIDDEN_PATH_PREFIXES.some((p) => p !== '/parent-app' && path.startsWith(p));
  if (hideExact || hidePrefix) return null;

  const onPfp = PFP_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
  // "With my therapist" is a sub-route of /parent-app but conceptually
  // the OTHER tab — split it out here so we highlight the correct chip.
  const isTherapistTab = path.startsWith('/parent-app/with-therapist')
    || path === '/'
    || path.startsWith('/children/');
  const clinicalActive = isTherapistTab;
  const pfpActive = onPfp && !isTherapistTab;

  const goClinical = () => {
    // Dual-role users always land on the in-PFP "With my therapist"
    // view. This keeps them inside the PFP surface (styling, header,
    // side-nav) instead of routing to `/` which ProtectedRoute bounces
    // parent_self users away from.
    navigate('/parent-app/with-therapist');
  };
  const goPfp = () => {
    navigate('/parent-app/home');
  };

  return (
    <div
      className="sticky top-0 z-50 border-b border-stone-200 bg-stone-100/90 backdrop-blur"
      data-testid="dual-role-tabs"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-2 px-4 py-2">
        <TabButton
          active={clinicalActive}
          onClick={goClinical}
          icon={Stethoscope}
          label="With my therapist"
          testId="dual-role-tab-clinical"
        />
        <TabButton
          active={pfpActive}
          onClick={goPfp}
          icon={Heart}
          label="At Home"
          testId="dual-role-tab-pfp"
        />
      </div>
    </div>
  );
}
